import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, resolveAsset } from '../api/client';
import { useT } from '../i18n';
import { trackEvent } from '../lib/track';

// Storefront promo banner. Fetches the currently-active promo (GET /api/promo,
// which returns null when none is enabled/in-window) and renders the
// admin-uploaded image with the bundle deal (old price struck-through, new
// price, CTA) overlaid on the image's empty right side. Renders nothing when
// there's no active promo or no image, so it's safe to mount unconditionally.
//
// `placement` ('mobile' | 'desktop') only adds a class used for responsive
// show/hide — the banner sits at the top on mobile and under the popular grid
// on desktop (see Home.jsx + the .promo-deal--* media queries in App.css).

const CUR = { ILS: '₪', USD: '$', EUR: '€' };

function fmtPrice(cents, currency = 'ILS') {
  const v = (cents || 0) / 100;
  const n = Number.isInteger(v) ? v : v.toFixed(2);
  return `${n} ${CUR[currency] || currency}`;
}

function booksLabel(qty, t) {
  return qty === 2 ? t.promoBundleDual : t.promoBundlePlural.replace('{n}', qty);
}

export default function PromoBanner({ to = '/c/heroes', placement }) {
  const t = useT();
  const [promo, setPromo] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get('/api/promo')
      .then(r => { if (alive) setPromo(r?.promo || null); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!promo || !promo.imageUrl) return null;

  const qty = promo.bundleQty || 2;
  const hasDiscount = promo.originalPriceCents && promo.originalPriceCents > promo.bundlePriceCents;
  const cls = ['promo-deal', placement && `promo-deal--${placement}`].filter(Boolean).join(' ');

  // Count banner clicks in the first-party analytics (admin Stats → "نقرات
  // البانر"). Fire-and-forget via sendBeacon/keepalive so it survives the
  // navigation the Link triggers. `placement` distinguishes mobile/desktop.
  const onClick = () => trackEvent('promo_click', { slug: 'promo-banner', name: placement || 'default' });

  return (
    <Link to={to} className={cls} aria-label={t.promoAriaLabel} onClick={onClick}>
      <img src={resolveAsset(promo.imageUrl)} alt={t.promoAriaLabel} className="promo-deal-img" />
      <div className="promo-deal-overlay">
        <span className="promo-deal-eyebrow">{t.promoEyebrow}</span>
        <span className="promo-deal-headline">{t.promoHeadline.replace('{books}', booksLabel(qty, t))}</span>
        <div className="promo-deal-prices">
          {hasDiscount && (
            <span className="promo-deal-old">{fmtPrice(promo.originalPriceCents, promo.currency)}</span>
          )}
          <span className="promo-deal-new">{fmtPrice(promo.bundlePriceCents, promo.currency)}</span>
        </div>
        <span className="promo-deal-cta">{t.promoCta}</span>
      </div>
    </Link>
  );
}
